import { useEffect } from 'react';
import { X } from 'lucide-react';

import { useGameSelection } from '@/hooks/useGameSelection';
import { useHaptics } from '@/hooks/useHaptics';
import { Loader } from '@/components/common/Loader';
import { cn } from '@/lib/cn';

interface GamePickerProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Full-screen overlay with a 3-col grid of available games. Tapping a card
 * dispatches the selection (mirrors `selectedGames` and `activeGames` in
 * Redux + persists to localStorage), fires a selection haptic, and closes
 * the picker. Esc/back also closes.
 */
export function GamePicker({ open, onClose }: GamePickerProps) {
  const { games, isLoading, activeGameId, selectGame, clearGame } = useGameSelection();
  const haptics = useHaptics();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const onPick = (id: number) => {
    haptics.selection();
    selectGame(id);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-bg">
      <header className="flex items-center justify-between border-b border-border px-4 py-3">
        <div>
          <h2 className="text-lg font-bold text-fg">Select your game</h2>
          <p className="mt-0.5 text-xs text-fg-muted">
            We'll filter tournaments and the Hub for you.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="flex h-9 w-9 items-center justify-center rounded-full bg-bg-secondary text-fg transition-opacity active:opacity-80"
        >
          <X aria-hidden className="h-5 w-5" />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <Loader />
        ) : games.length === 0 ? (
          <div className="rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
            No games available right now.
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            {games.map((game) => {
              const selected = game.id === activeGameId;
              return (
                <button
                  key={game.id}
                  type="button"
                  onClick={() => onPick(game.id)}
                  className={cn(
                    'group relative flex aspect-square w-full items-end overflow-hidden rounded-xl bg-bg-secondary p-2 text-left',
                    'transition-opacity active:opacity-80',
                    selected && 'ring-2 ring-brand',
                  )}
                >
                  {game.icon && (
                    <img
                      src={game.icon}
                      alt=""
                      loading="lazy"
                      className={cn(
                        'absolute inset-0 h-full w-full object-cover transition-[filter] duration-200',
                        !selected && 'grayscale-[35%]',
                      )}
                    />
                  )}
                  <span
                    className={cn(
                      'pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-1.5',
                      'text-[11px] font-bold uppercase tracking-wide text-white',
                    )}
                  >
                    {game.name}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {activeGameId !== null && (
          <button
            type="button"
            onClick={() => {
              haptics.selection();
              clearGame();
              onClose();
            }}
            className="mt-6 w-full rounded-xl border border-border bg-bg-secondary py-3 text-sm font-medium text-fg-muted transition-opacity active:opacity-80"
          >
            Show all games
          </button>
        )}
      </div>
    </div>
  );
}
